import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, Text, useToast } from '@chakra-ui/react';
import axios from 'axios';
import { Action, Page } from '@/../../server/types/loggingTypes';
import { PrivacyLevel } from '@/../../shared/types/notesTypes';
import defaultProfilePic from '@/assets/profileAnonPicInverse.png';
import { useOnboardingCompletionNavigation } from '@/hooks/useAuthBasedNavigation';
import useRouterNavigation from '@/hooks/useRouterNavigation';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setAuthInfo } from '@/store/slices/authSlice';
import { sizing } from '@/styles/sizing';
import { getStatsigClient } from '@/utils/statsig';
import NoteTextarea from './NoteTextarea';

interface AddOrUpdateNoteProps {
  noteId?: string;
}

function AddOrUpdateNote({ noteId }: AddOrUpdateNoteProps) {
  const authState = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const [newNoteContent, setNewNoteContent] = useState('');
  const originalNoteContent = useRef<string>();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { back } = useRouterNavigation();
  const [isNotePublic, setIsNotePublic] = useState(true);

  const profilePic = authState.profilePic || defaultProfilePic;

  useEffect(() => {
    getStatsigClient().logEvent(Page.NOTES_PAGE, Action.VIEW);
  }, []);

  useOnboardingCompletionNavigation();

  useEffect(() => {
    try {
      if (!noteId) {
        return;
      }
      axios.get(`/api/notes/${noteId}`).then((response) => {
        if (response.status !== 200) {
          throw new Error('Failed to create note');
        }
        setNewNoteContent(response.data.data.content);
        if (response.data.data.privacyLevel === PrivacyLevel.CONNECTION_ONLY) {
          setIsNotePublic(false);
        }
        originalNoteContent.current = response.data.data.content;
      });
    } catch (e) {
      console.error(e);
    }
  }, [noteId]);

  const handleSaveNote = async () => {
    try {
      if (newNoteContent.length === 0) {
        toast({
          title: 'Failed to save note',
          description: 'Your note cannot be empty',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'bottom',
          variant: 'subtle',
          colorScheme: 'red',
        });
        return;
      }

      let response = null;
      if (noteId) {
        response = await axios.patch(`/api/notes/${noteId}`, {
          content: newNoteContent === originalNoteContent.current ? undefined : newNoteContent,
          privacyLevel: isNotePublic ? PrivacyLevel.PUBLIC : PrivacyLevel.CONNECTION_ONLY,
        });
      } else {
        // Prepare the new note object based on the Sequelize model
        const newNote = {
          userId: authState.userId, // the user signed in and userId from localstorage,
          relatedAskId: null,
          //relatedOnboardingQuestionId: null,
          content: newNoteContent, // Ensure this variable contains the note content
          // embedding: embeddingData.embedding, handled in backend
          embeddingModel: null,
          tokenCount: null,
          tokenCost: null,
          status: 'ENABLED',
          isArchived: false,
          privacyLevel: isNotePublic ? PrivacyLevel.PUBLIC : PrivacyLevel.CONNECTION_ONLY,
          createDate: new Date().toISOString(),
          noteType: 'NOTE',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        response = await axios.post('/api/notes', newNote);
      }

      setNewNoteContent('');
      getStatsigClient().logEvent('add_note', newNoteContent);
      if (!authState.isOnBoardingTutorialComplete) {
        dispatch(
          setAuthInfo({
            numNotesOrQuestionsAnswered: authState.numNotesOrQuestionsAnswered + 1,
          }),
        );
      }

      if (response.data.justCompletedOnboardingTutorial) {
        dispatch(
          setAuthInfo({
            isOnBoardingTutorialComplete: true,
            justCompletedOnboardingTutorial: true,
          }),
        );
        toast({
          title: 'Congratulations',
          description: 'You can now ask your profile a question!',
          status: 'success',
          duration: 3000,
          position: 'top',
          isClosable: true,
          render: () => (
            <Box color="white" p={3} bg="#1C1C1C">
              <Text fontWeight="bold">Congratulations</Text>
              <Text>You can now ask your profile a question!</Text>
            </Box>
          ),
        });
      } else {
        toast({
          title: 'Note Added',
          description: 'Your note has been successfully saved.',
          status: 'success',
          duration: 1500,
          isClosable: true,
          position: 'bottom',
          render: () => (
            <Box color="white" p={3} bg="#1C1C1C">
              <Text fontWeight="bold">Note Added</Text>
              <Text>Your note has been successfully saved.</Text>
            </Box>
          ),
        });
        back();
      }
    } catch (error) {
      console.error('Error saving note:', error);
    }
  };

  const handleDeleteNote = async () => {
    console.log('Deleting note with ID:', noteId);

    try {
      console.log('Deleting note with ID:', noteId);
      const response = await axios.delete(`/api/notes/${noteId}`);

      if (response.status === 200) {
        // Display the toast notification
        toast({
          title: 'Note Deleted',
          description: 'The note has been deleted.',
          status: 'success',
          duration: 2000,
          isClosable: true,
          render: () => (
            <Box color="white" p={3} bg="#1C1C1C">
              <Text fontWeight="bold">Note Deleted</Text>
              <Text>The note has been deleted.</Text>
            </Box>
          ),
        });
        navigate({ to: '/' });
      } else {
        // Handle non-OK responses here
        console.error('Server responded with non-OK status');
      }
    } catch (error) {
      console.error('Error disabling note:', error);
    }
  };

  const setNoteContent = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setNewNoteContent(event.target.value);
    getStatsigClient().logEvent('note_typing_changed', event.target.value);
  };

  return (
    <Box bg="#1C1C1C" minH="100vh">
      <Box maxWidth={sizing.maxAppContentWidthPx} width="100%" mx="auto" p={4} textAlign="left">
        <Flex align="center" justify="space-between" mb={8}>
          <Button
            leftIcon={<ChevronLeftIcon boxSize={5} left={0} right={0} p={0} />}
            alignSelf="flex-start"
            marginBottom={8}
            onClick={() => back()}
            backgroundColor="#1C1C1C"
            color="white"
            borderRadius="md"
            _hover={{ bg: '#2C2C2C' }}
            size="md"
          >
            Back
          </Button>
        </Flex>
        <NoteTextarea
          profilePic={profilePic}
          noteContent={newNoteContent}
          setNoteContent={setNoteContent}
          onSaveNote={handleSaveNote}
          isNotePublic={isNotePublic}
          setIsNotePublic={setIsNotePublic}
        />

        <Box position="fixed" bottom="0" left="0" right="0" bg="#1c1c1c" p={4} textAlign="center">
          <Button
            onClick={handleSaveNote}
            maxWidth={sizing.maxAppContentWidthPx}
            width="100%"
            height="60px"
            bg="#FF0050"
            color="white"
            borderRadius="full"
            _hover={{
              bg: '#FF3371',
              boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.15)',
              transform: 'translateY(-1px)',
            }}
            fontSize={{ base: 'sm', md: 'md', lg: 'lg' }}
          >
            Add to Your Profile
          </Button>
        </Box>

        {noteId && (
          <Box mt={4} textAlign="center">
            <Button
              onClick={handleDeleteNote}
              backgroundColor="transparent"
              color="white"
              borderRadius="md"
              _hover={{ bg: '#2C2C2C' }}
            >
              Delete Note
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default AddOrUpdateNote;
